import { useAuth } from '@commander/auth';
import { logger } from '@commander/logger';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Label,
  LexicalViewer,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Textarea,
  toast,
} from '@commander/ui';
import { PUSHER_CHANNELS, PUSHER_EVENTS, usePusher } from '@commander/utils';
import { RiCheckLine, RiCloseLine, RiRobot2Line, RiSparklingLine } from '@remixicon/react';
import { useEffect, useRef, useState } from 'react';
import {
  useEmailAiGeneration,
  useGenerateEmail,
  useGenerateSms,
  useSmsAiGeneration,
} from '../../hooks';
import { ensureSigningLink } from '../../utils/ensureSigningLink';

interface AiContentGeneratorProps {
  type: 'email' | 'sms';
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onContentGenerated: (content: { subject?: string; body: any }) => void;
  disabled?: boolean;
  buttonText?: string;
  buttonClassName?: string;
  buttonVariant?: 'default' | 'outline';
  iconOnly?: boolean;
  contactId?: string;
  recipientName?: string;
  masterCompanyLocationId?: string;
  quoteId?: string;
  /**
   * Reports the signing URL the backend resolved for `quoteId`, which may be
   * minted after the composer opened. `resolvedQuoteId` is the quote the URL
   * belongs to, so the receiver can drop one that no longer matches its own quote.
   */
  onSigningUrlResolved?: (signingUrl: string, resolvedQuoteId?: string) => void;
  /**
   * When set, runs generation immediately on mount using this text as the
   * prompt (e.g. a Next Steps action's own description), and applies the
   * result straight into the composer once it completes instead of showing
   * the manual preview/insert step. Consumed once per composer session.
   */
  autoGeneratePrompt?: string;
  /**
   * Unique token identifying this particular auto-generate request, distinct
   * from the prompt text itself. Two different Next Steps actions can carry
   * the same templated description; keying the "already triggered" guard on
   * the prompt string would then wrongly suppress the second request when
   * the composer stays mounted between them. Falls back to autoGeneratePrompt
   * when omitted.
   */
  autoGenerateKey?: string;
}

export function AiContentGenerator({
  type,
  isOpen,
  onOpenChange,
  onContentGenerated,
  disabled = false,
  buttonText,
  buttonClassName = '',
  buttonVariant = 'default',
  iconOnly = false,
  contactId,
  recipientName,
  masterCompanyLocationId,
  quoteId,
  onSigningUrlResolved,
  autoGeneratePrompt,
  autoGenerateKey,
}: AiContentGeneratorProps) {
  const [aiPrompt, setAiPrompt] = useState('');
  // True from the moment an auto-triggered generation starts until its result
  // is applied — lets the completion handler apply the draft directly instead
  // of stopping at the manual preview step, without affecting a later manual
  // regenerate in the same composer session.
  const autoApplyRef = useRef(false);
  const autoTriggeredRef = useRef<string | undefined>(undefined);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState<any | null>(null);
  // The in-flight generation and the quote it was started for. The composer is a
  // long-lived singleton, so a different quote can be loaded into it while a
  // generation is still running; results for a superseded quote are discarded.
  const [generation, setGeneration] = useState<{ id: string; quoteId?: string } | null>(null);
  const [generatedSubject, setGeneratedSubject] = useState<string>('');

  const generationId = generation?.id ?? null;
  const activeQuoteId = typeof quoteId === 'string' ? quoteId : undefined;

  // The completion handler awaits a refetch, during which the composer can be
  // switched to another quote; the closure's `activeQuoteId` is frozen at bind
  // time, so the re-check after the await reads the quote through this ref.
  const activeQuoteIdRef = useRef(activeQuoteId);
  useEffect(() => {
    activeQuoteIdRef.current = activeQuoteId;
  }, [activeQuoteId]);

  // The quote the previewed body and its embedded signing link were written for.
  // The preview outlives the generation, so the composer can be switched to
  // another quote while it sits open — and the composer leaves an already-linked
  // body alone on insert, so a stale link would ship as-is.
  const generatedQuoteIdRef = useRef<string | undefined>(undefined);

  // Hooks
  const { user } = useAuth();
  const generateEmailMutation = useGenerateEmail();
  const generateSmsMutation = useGenerateSms();

  // Use type-specific AI generation hooks (enabled: false because we manually refetch via Pusher event)
  const emailGeneration = useEmailAiGeneration(generationId || '', { enabled: false });
  const smsGeneration = useSmsAiGeneration(generationId || '', { enabled: false });
  const { refetch: refetchGeneration } = type === 'email' ? emailGeneration : smsGeneration;

  const { subscribe, unsubscribe } = usePusher();

  // Get the appropriate mutation based on type
  const generateMutation = type === 'email' ? generateEmailMutation : generateSmsMutation;

  // Get prompt limits based on type
  const promptMaxLength = type === 'email' ? 2500 : 500;
  const promptMinLength = 10;

  // Helper to determine if generation is allowed
  // Email: contactId OR valid prompt is sufficient
  // SMS: always requires a valid prompt
  const canGenerate =
    type === 'email'
      ? Boolean(contactId) || aiPrompt.trim().length >= promptMinLength
      : aiPrompt.trim().length >= promptMinLength;

  // Pusher real-time listener for AI generation completion
  useEffect(() => {
    if (!generation || !user?.id) return;

    const { id: generationId, quoteId: generationQuoteId } = generation;
    const channelName = PUSHER_CHANNELS.USER(user.id);
    const channel = subscribe(channelName);

    const handleCompleted = async (data: any) => {
      if (data.generationId === generationId) {
        if (generationQuoteId !== activeQuoteId) {
          logger.info(`[AiContentGenerator:${type}] Discarding result for a superseded quote`, {
            generationId,
            generationQuoteId,
            quoteId: activeQuoteId,
          });
          setGeneration(null);
          setIsGenerating(false);
          return;
        }

        logger.info(`[AiContentGenerator:${type}] AI generation completed`, { generationId });
        // Fetch the full generation result
        const result = await refetchGeneration();

        // The composer may have moved to another quote while the refetch was in
        // flight — re-check before anything here touches the composer state.
        if (generationQuoteId !== activeQuoteIdRef.current) {
          logger.info(
            `[AiContentGenerator:${type}] Discarding result for a quote superseded during refetch`,
            { generationId, generationQuoteId, quoteId: activeQuoteIdRef.current }
          );
          setGeneration(null);
          setIsGenerating(false);
          return;
        }

        if (result.data) {
          // Validate body content
          const body = result.data.body;

          logger.info(`[AiContentGenerator:${type}] Received generation data`, {
            generationId,
            hasBody: !!body,
            bodyType: typeof body,
            bodyKeys: body && typeof body === 'object' ? Object.keys(body) : null,
            subject: result.data.subject,
          });

          // For email, validate that body is a valid Lexical state
          if (type === 'email') {
            if (!body || typeof body !== 'object' || !body.root) {
              logger.error('[AiContentGenerator:email] Invalid Lexical state received', { body });
              setGeneration(null);
              setIsGenerating(false);
              toast.error('Generated email has invalid format. Please try again.');
              return;
            }
          }

          if (result.data.signingUrl) {
            onSigningUrlResolved?.(result.data.signingUrl, generationQuoteId);
          }

          // Preview the body the composer will actually insert. The composer
          // re-asserts the link on insert regardless; that second pass finds the
          // link node this one appended and leaves the body alone.
          const finalBody =
            type === 'email' ? ensureSigningLink(body, result.data.signingUrl) : body;
          const finalSubject = type === 'email' ? result.data.subject : undefined;
          generatedQuoteIdRef.current = generationQuoteId;
          setIsGenerating(false);

          if (autoApplyRef.current) {
            // Auto-triggered: skip the manual preview/insert step entirely.
            autoApplyRef.current = false;
            onContentGenerated({ subject: finalSubject, body: finalBody });
            setGeneration(null);
            setAiPrompt('');
            onOpenChange(false);
            toast.success(`${type === 'email' ? 'Email' : 'SMS'} draft generated!`);
          } else {
            setGeneratedContent(finalBody);
            if (type === 'email' && finalSubject) {
              setGeneratedSubject(finalSubject);
            }
            toast.success(`${type === 'email' ? 'Email' : 'SMS'} generated successfully!`);
          }
        }
      }
    };

    const handleFailed = (data: any) => {
      if (data.generationId === generationId) {
        logger.error(`[AiContentGenerator:${type}] AI generation failed`, {
          generationId,
          error: data.error,
        });
        setIsGenerating(false);
        autoApplyRef.current = false;
        toast.error(data.error || `Failed to generate ${type}`);
      }
    };

    channel.bind(PUSHER_EVENTS.AI_GENERATION_COMPLETED, handleCompleted);
    channel.bind(PUSHER_EVENTS.AI_GENERATION_FAILED, handleFailed);

    return () => {
      channel.unbind(PUSHER_EVENTS.AI_GENERATION_COMPLETED, handleCompleted);
      channel.unbind(PUSHER_EVENTS.AI_GENERATION_FAILED, handleFailed);
      unsubscribe(channelName);
    };
  }, [
    generation,
    activeQuoteId,
    user?.id,
    subscribe,
    unsubscribe,
    refetchGeneration,
    type,
    onSigningUrlResolved,
    onContentGenerated,
    onOpenChange,
  ]);

  const handleGenerate = async (promptOverride?: string) => {
    const promptToUse = promptOverride ?? aiPrompt;
    const hasValidPrompt = promptToUse.trim().length >= promptMinLength;
    const hasContactId = Boolean(contactId);

    // For email: either contactId OR valid prompt is sufficient
    // For SMS: always requires a valid prompt
    if (type === 'email') {
      if (!hasContactId && !hasValidPrompt) {
        toast.error(`Please enter a prompt (at least ${promptMinLength} characters)`);
        autoApplyRef.current = false;
        return;
      }
    } else if (!hasValidPrompt) {
      toast.error(`Please enter a prompt (at least ${promptMinLength} characters)`);
      autoApplyRef.current = false;
      return;
    }

    if (promptOverride !== undefined) {
      setAiPrompt(promptOverride);
    }

    try {
      setIsGenerating(true);
      setGeneratedContent(null);
      generatedQuoteIdRef.current = undefined;
      setGeneratedSubject('');

      logger.info(`[AiContentGenerator:${type}] Starting AI generation`, {
        promptLength: promptToUse.length,
        hasContactId: !!contactId,
        hasRecipientName: !!recipientName,
      });

      const result = await generateMutation.mutateAsync({
        prompt: promptToUse.trim() || undefined,
        contactId: typeof contactId === 'string' ? contactId : undefined,
        recipientName: typeof recipientName === 'string' ? recipientName : undefined,
        masterCompanyLocationId:
          typeof masterCompanyLocationId === 'string' ? masterCompanyLocationId : undefined,
        quoteId: activeQuoteId,
      });
      setGeneration({ id: result.id, quoteId: activeQuoteId });

      logger.info(`[AiContentGenerator:${type}] AI generation queued`, {
        generationId: result.id,
      });
    } catch (error: any) {
      logger.error(`[AiContentGenerator:${type}] Failed to start AI generation`, { error });
      setIsGenerating(false);
      autoApplyRef.current = false;
      toast.error(error.message || `Failed to generate ${type}`);
    }
  };

  // Auto-run generation once per distinct trigger (e.g. a Next Steps
  // "Compose Follow Up Email" action), so the draft is ready without the rep
  // manually opening this popover and clicking Generate themselves. Tracking
  // by autoGenerateKey (not the prompt text) lets a new request on an
  // already-mounted composer trigger again even when its prompt text happens
  // to match a previous request's, while still guarding against re-firing
  // for the same request.
  // biome-ignore lint/correctness/useExhaustiveDependencies: only re-run when the trigger changes; handleGenerate/onOpenChange are recreated each render and including them would refire this
  useEffect(() => {
    const triggerKey = autoGenerateKey ?? autoGeneratePrompt;
    if (!autoGeneratePrompt || !triggerKey || autoTriggeredRef.current === triggerKey) return;
    autoTriggeredRef.current = triggerKey;
    autoApplyRef.current = true;
    onOpenChange(true);
    void handleGenerate(autoGeneratePrompt);
  }, [autoGeneratePrompt, autoGenerateKey]);

  const handleUseGeneratedContent = () => {
    if (!generatedContent) return;

    if (generatedQuoteIdRef.current !== activeQuoteIdRef.current) {
      logger.info(`[AiContentGenerator:${type}] Discarding a preview for a superseded quote`, {
        generatedQuoteId: generatedQuoteIdRef.current,
        quoteId: activeQuoteIdRef.current,
      });
      setGeneratedContent(null);
      generatedQuoteIdRef.current = undefined;
      setGeneratedSubject('');
      toast.error('This draft was written for a different quote. Please generate a new one.');
      return;
    }

    onContentGenerated({
      subject: type === 'email' ? generatedSubject : undefined,
      body: generatedContent,
    });

    // Reset state
    setGeneratedContent(null);
    generatedQuoteIdRef.current = undefined;
    setGeneratedSubject('');
    setAiPrompt('');
    setGeneration(null);
    setIsGenerating(false);
    onOpenChange(false);

    toast.success('AI-generated content applied!');
  };

  const handleRegenerate = () => {
    setGeneration(null);
    setGeneratedContent(null);
    generatedQuoteIdRef.current = undefined;
    setGeneratedSubject('');
    setIsGenerating(false);
  };

  const defaultButtonText = buttonText || `Write ${type === 'email' ? 'Email' : 'SMS'} with AI`;

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange} modal={false}>
      <PopoverTrigger asChild>
        <Button
          variant={buttonVariant}
          size={iconOnly ? 'icon' : 'sm'}
          type="button"
          className={buttonClassName}
          disabled={disabled || isGenerating}
        >
          <RiRobot2Line className={iconOnly ? 'h-4 w-4' : 'h-4 w-4 mr-2'} />
          {!iconOnly && (isGenerating ? 'Generating...' : defaultButtonText)}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96" align="end">
        {/* Input State */}
        {!isGenerating && !generatedContent && (
          <div className="space-y-3">
            <div>
              <Label htmlFor="ai-prompt" className="text-sm font-medium">
                What would you like to write?
                {type === 'email' && contactId && (
                  <span className="text-muted-foreground font-normal ml-1">(optional)</span>
                )}
              </Label>
              <p className="text-xs text-muted-foreground mt-1 mb-2">
                {type === 'email'
                  ? contactId
                    ? 'Describe the email you want, or leave blank to auto-generate based on contact context'
                    : 'Describe the email you want AI to write for you'
                  : 'Describe the SMS message you want AI to write'}
              </p>
            </div>
            <Textarea
              id="ai-prompt"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder={
                type === 'email'
                  ? contactId
                    ? 'Optional: Add specific instructions, or leave blank for auto-generation...'
                    : 'e.g., Write a follow-up email thanking them for the meeting...'
                  : "e.g., Follow up with the client about yesterday's meeting and ask when they're available for a call"
              }
              className="min-h-[100px] resize-none"
              maxLength={promptMaxLength}
              disabled={isGenerating}
            />
            <p className="text-xs text-muted-foreground">
              {aiPrompt.length}/{promptMaxLength} characters
            </p>
            <Button
              onClick={() => handleGenerate()}
              className="w-full"
              size="sm"
              disabled={!canGenerate}
            >
              <RiSparklingLine className="h-4 w-4 mr-2" />
              {type === 'email' && contactId && !aiPrompt.trim()
                ? 'Auto-Generate Email'
                : `Generate ${type === 'email' ? 'Email' : 'SMS'}`}
            </Button>
          </div>
        )}

        {/* Loading State */}
        {isGenerating && (
          <div className="flex flex-col items-center justify-center py-8 space-y-3">
            <Spinner className="h-8 w-8" />
            <p className="text-sm text-muted-foreground">
              Generating your {type === 'email' ? 'email' : 'SMS'}...
            </p>
          </div>
        )}

        {/* Preview State */}
        {!isGenerating && generatedContent && (
          <div className="space-y-3">
            <div>
              <Label className="text-sm font-medium">
                Generated {type === 'email' ? 'Email' : 'SMS'}
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                Preview of your AI-generated content
              </p>
            </div>

            {/* Email: Show subject + body */}
            {type === 'email' && (
              <>
                {generatedSubject && (
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Subject</Label>
                    <div className="border rounded-md p-2 bg-background text-sm">
                      {generatedSubject}
                    </div>
                  </div>
                )}
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Body</Label>
                  <div className="border rounded-md p-3 bg-muted/50 max-h-[200px] overflow-y-auto">
                    <LexicalViewer serializedState={generatedContent} />
                  </div>
                </div>
              </>
            )}

            {/* SMS: Show plain text */}
            {type === 'sms' && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <RiSparklingLine className="h-4 w-4" />
                    Generated SMS
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="p-3 bg-muted rounded-md">
                    <p className="text-sm whitespace-pre-wrap">{generatedContent}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="flex gap-2">
              <Button onClick={handleUseGeneratedContent} className="flex-1" size="sm">
                <RiCheckLine className="h-4 w-4 mr-1" />
                {type === 'email' ? 'Insert into Email' : 'Use This'}
              </Button>
              <Button onClick={handleRegenerate} variant="outline" className="flex-1" size="sm">
                <RiCloseLine className="h-4 w-4 mr-1" />
                {type === 'email' ? 'Regenerate' : 'Discard'}
              </Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
